import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Box, Error, Label, Warning } from 'shared/ui'
import {
  makeTextFieldAutoFocus,
  makeTextFieldHighlight,
  makeTextFieldHighlightLimit,
  TextField,
} from 'shared/ui/TextField'
import { InputPassword } from 'shared/ui/TextField/InputPresets/InputPassword'
import { MAX_PASSWORD_LENGTH } from 'shared/constants/auth'
import { type ProfileStore } from 'pages/settings/pages/personal/pages/account/pages/profile'

type IChangePasswordModalProps = {
  store: ProfileStore
}

export const ChangePasswordModal = observer(({ store }: IChangePasswordModalProps) => {
  const {
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmNewPassword,
    setConfirmNewPassword,
    errorsMap,
    loading,
  } = store
  const currentPasswordError = errorsMap.get('current_password')
  const newPasswordError = errorsMap.get('new_password')
  const [newPasswordLimitReached, setNewPasswordLimitReached] = useState(false)
  const [confirmNewPasswordLimitReached, setConfirmNewPasswordLimitReached] = useState(false)

  return (
    <Box marginTop={'-10px'}>
      <Box marginBottom={'15px'}>
        <Label
          label={{
            text: 'Current password',
          }}
          marginBottom={'5px'}
        />
        <TextField
          Input={InputPassword}
          variant='stroke'
          InputProps={{
            placeholder: 'Enter your current password',
          }}
          error={!!currentPasswordError}
          disabled={loading}
          size='medium'
          onChange={setCurrentPassword}
          value={currentPassword}
          mainActions={[
            makeTextFieldAutoFocus({ withFocus: true }),
            makeTextFieldHighlight({ mode: 'error', highlight: !!currentPasswordError }),
          ]}
        />
        <Error type={'text'} error={currentPasswordError} />
      </Box>

      <Box marginBottom={'15px'}>
        <Label
          label={{
            text: 'New password',
          }}
          marginBottom={'5px'}
        />
        <TextField
          Input={InputPassword}
          variant='stroke'
          InputProps={{
            placeholder: 'Create a new password',
          }}
          error={!!newPasswordError}
          disabled={loading}
          size='medium'
          onChange={setNewPassword}
          value={newPassword}
          mainActions={[
            makeTextFieldHighlight({ mode: 'error', highlight: !!newPasswordError }),
            makeTextFieldHighlightLimit({
              limit: MAX_PASSWORD_LENGTH,
              onChange: setNewPasswordLimitReached,
            }),
          ]}
        />
        <Error type={'text'} error={newPasswordError} />
        {newPasswordLimitReached && (
          <Warning text={`Maximum password length is ${MAX_PASSWORD_LENGTH} characters`} />
        )}
      </Box>

      <Box>
        <Label
          label={{
            text: 'Confirm new password',
          }}
          marginBottom={'5px'}
        />
        <TextField
          Input={InputPassword}
          variant='stroke'
          InputProps={{
            placeholder: 'Re-enter your new password',
          }}
          disabled={loading}
          size='medium'
          onChange={setConfirmNewPassword}
          value={confirmNewPassword}
          mainActions={[
            makeTextFieldHighlightLimit({
              limit: MAX_PASSWORD_LENGTH,
              onChange: setConfirmNewPasswordLimitReached,
            }),
          ]}
        />
        {confirmNewPasswordLimitReached && (
          <Warning text={`Maximum password length is ${MAX_PASSWORD_LENGTH} characters`} />
        )}
      </Box>
    </Box>
  )
})
